<script lang="ts">
  import type { HTMLAttributes } from 'vue'

  export interface SearchInputProps {
    /** The search value (v-model). */
    modelValue?: string | null
    /** Input placeholder shown when empty. */
    placeholder?: string
    /** Disable the input. */
    disabled?: boolean
    /** HTML `id` of the underlying input. */
    id?: string
    /** Test id forwarded to the underlying input. */
    dataTestid?: string
    /** Focus the input on mount. */
    autofocus?: boolean
    /** Show a clear (✕) button when the input has a value. */
    clearable?: boolean
    /** Show a loading spinner in the trailing addon. */
    loading?: boolean
    /**
     * Render the bordered InputGroup box. Set `false` to blend the field
     * into a custom container (e.g. a borderless search bar) — strips the
     * border, background, shadow, rounding, and focus ring.
     */
    bordered?: boolean
    /** Class forwarded to the outer InputGroup. */
    class?: HTMLAttributes['class']
    /** Class forwarded to the inner input. */
    inputClass?: HTMLAttributes['class']
  }
</script>

<script setup lang="ts">
  import { computed, ref } from 'vue'
  import { IconClose, IconLoading, IconSearch } from '@gat-ship/icons'
  import {
    InputGroup,
    InputGroupAddon,
    InputGroupButton,
    InputGroupInput
  } from '../ui/input-group'
  import { cn } from '../../lib/utils'

  const props = withDefaults(defineProps<SearchInputProps>(), {
    clearable: false,
    loading: false,
    bordered: true
  })

  const emit = defineEmits<{
    (e: 'update:modelValue', value: string): void
    (e: 'click:clear'): void
    (e: 'focus', event: FocusEvent): void
    (e: 'blur', event: FocusEvent): void
    (e: 'keydown', event: KeyboardEvent): void
  }>()

  const inputRef = ref<{ focus: () => void } | null>(null)
  const focused = ref(false)

  function focus() {
    inputRef.value?.focus()
  }

  function onFocus(event: FocusEvent) {
    focused.value = true
    emit('focus', event)
  }

  function onBlur(event: FocusEvent) {
    focused.value = false
    emit('blur', event)
  }

  function clear() {
    emit('update:modelValue', '')
    emit('click:clear')
    focus()
  }

  const showClear = computed(
    () => props.clearable && !!props.modelValue && !props.disabled
  )

  const groupClass = computed(() =>
    props.bordered
      ? props.class
      : cn(
          'h-auto rounded-none border-0 bg-transparent shadow-none dark:bg-transparent',
          'has-[[data-slot=input-group-control]:focus-visible]:border-transparent',
          'has-[[data-slot=input-group-control]:focus-visible]:ring-0',
          props.class
        )
  )

  defineExpose({
    focus,
    isFocused: computed(() => focused.value)
  })
</script>

<template>
  <InputGroup :class="groupClass">
    <InputGroupAddon>
      <slot name="prepend">
        <IconSearch class="text-muted-foreground size-4" />
      </slot>
    </InputGroupAddon>
    <InputGroupInput
      :id="id"
      ref="inputRef"
      :model-value="modelValue ?? ''"
      :placeholder="placeholder"
      :disabled="disabled"
      :autofocus="autofocus"
      :data-testid="dataTestid"
      :class="inputClass"
      @update:model-value="emit('update:modelValue', String($event))"
      @focus="onFocus"
      @blur="onBlur"
      @keydown="emit('keydown', $event)"
    />
    <InputGroupAddon
      v-if="loading || showClear || $slots.append"
      align="inline-end"
    >
      <IconLoading
        v-if="loading"
        class="text-muted-foreground size-4 animate-spin"
      />
      <InputGroupButton v-if="showClear" aria-label="Clear" @click="clear">
        <IconClose class="size-4" />
      </InputGroupButton>
      <slot name="append" />
    </InputGroupAddon>
  </InputGroup>
</template>
